import { useRef, useState } from "react";
import {
  Text,
  Box,
  Icon,
  Container,
  VStack,
  Heading,
  Button,
  useBreakpointValue,
  SimpleGrid,
  AspectRatio,
  useColorModeValue,
  Image,
  Stack,
  Card,
  CardBody,
  HStack,
  List,
  ListItem,
  ListIcon,
} from "@chakra-ui/react";
import { useSpring, animated } from "@react-spring/web";
import { Link } from "react-router-dom";
import CarouselImage from "../../components/carouselImage/CarouselImage";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade } from "swiper/modules";
import {
  StarIcon,
  CheckCircleIcon,
  TimeIcon,
  EmailIcon,
  ChatIcon,
  CalendarIcon,
  UnlockIcon,
  TriangleUpIcon,
  SettingsIcon,
} from "@chakra-ui/icons";
import "swiper/css";
import pathImages from "./pathImages.json";
import { Helmet } from "react-helmet-async";

const Home = () => {
  const progressBarRef = useRef(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [loadedImagesCount, setLoadedImagesCount] = useState(0);
  const isMobile = useBreakpointValue({ base: true, lg: false });

  // Colori per dark mode
  const bgCard = useColorModeValue("gray.900", "gray.800");
  const textColor = useColorModeValue("gray.100", "gray.200");
  const secondaryTextColor = useColorModeValue("gray.300", "gray.400");
  const borderColor = useColorModeValue("orange.400", "orange.300");
  const accentColor = useColorModeValue("orange.500", "orange.400");
  const bgGradient = useColorModeValue(
    "linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 100%)",
    "linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 100%)",
  );

  const springs = useSpring({
    from: { opacity: 0, y: 20 },
    to: { opacity: 1, y: 0 },
    config: { tension: 280, friction: 60 },
  });

  const onAutoplayTimeLeft = (s, time, progress) => {
    if (progressBarRef.current) {
      progressBarRef.current.style.width = `${(1 - progress) * 100}%`;
    }
  };

  const handleImageLoad = () => {
    const newCount = loadedImagesCount + 1;
    setLoadedImagesCount(newCount);
    if (newCount === pathImages.length) {
      setImagesLoaded(true);
    }
  };

  return (
    <animated.div style={{ ...springs }}>
      <Helmet>
        <title>
          Service Luci Firenze | Noleggio Luci per Eventi - Controluce
          Illuminazione
        </title>
        <meta
          name="description"
          content="Service luci a Firenze e servizio noleggio luci professionale per eventi. Controluce Illuminazione offre soluzioni complete di illuminazione per matrimoni, concerti e eventi aziendali. Preventivo gratuito."
        />
        <meta
          name="keywords"
          content="Firenze, service luci, servizio noleggio luci, noleggio luci Firenze, illuminazione eventi, service luci Firenze, noleggio attrezzatura luci, eventi Firenze"
        />
        <link rel="canonical" href="https://www.controluceilluminazione.it/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: "Controluce Illuminazione di Monti Daniele",
            description:
              "Service luci a Firenze e servizio noleggio luci professionale per eventi",
            image: [
              "https://www.controluceilluminazione.it/static/media/controluceLogoRWhite.e12538b1615ec247c4cc.png",
              "https://controluceilluminazione.it/static/media/controluceLogoLWhite.e92eabff7d445a556cd2.png",
            ],
            telephone: "+39 055 0935053",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Firenze",
              addressRegion: "Toscana",
              postalCode: "50018",
              streetAddress: "Via di casellina",
              addressCountry: "IT",
            },
            url: "https://www.controluceilluminazione.it",
            openingHours: "Mo-Fr 09:00-18:00",
            geo: {
              "@type": "GeoCoordinates",
              latitude: "43.7583291",
              longitude: "11.1626199",
            },
            areaServed: "Firenze e provincia",
            makesOffer: {
              "@type": "Offer",
              name: "Service luci e noleggio luci per eventi",
            },
            sameAs: [
              "https://www.facebook.com/p/Controluce-Illuminazione-100043546856066",
              "http://instagram.com/controluce_illuminazione/#",
            ],
          })}
        </script>
      </Helmet>

      {/* Quote & CTA Section */}
      <Container
        maxW="container.xl"
        px={{ base: "0.3em  ", md: 8, lg: 12 }}
        mb={{ base: 12, lg: "3em" }}
      >
        <VStack
          spacing={{ base: "1em", lg: 12 }}
          align="center"
          textAlign={{ base: "start", lg: "center" }}
        >
          <Box maxW="800px">
            <Heading
              as="blockquote"
              fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
              color={textColor}
              fontWeight="light"
              fontStyle="italic"
              lineHeight="1.6"
              mt={{ base: 6, lg: "1.2em" }}
              mb={{ base: 1, lg: "0em" }}
              position="relative"
              pl={{ base: "0.3em", lg: 8 }}
            >
              "E' il tocco magico della luce, sapientemente dosata, che rende
              ciò che si vuol mostrare, soggetto."
            </Heading>
          </Box>
        </VStack>
      </Container>
      {/* Hero Carousel Section */}
      <Box position="relative" overflow="hidden" mb={{ base: 8, lg: 12 }}>
        <Box
          className="autoplay-progress"
          position="absolute"
          top={0}
          left={0}
          right={0}
          zIndex={10}
        >
          <Box
            ref={progressBarRef}
            h="2px"
            bg="linear-gradient(90deg, #FFB300 0%, #FF8F00 100%)"
            w={0}
            transition="width 0.1s linear"
          />
        </Box>

        <Swiper
          style={{
            background: bgGradient,
          }}
          autoplay={{
            delay: 4500,
            disableOnInteraction: false,
          }}
          onAutoplayTimeLeft={onAutoplayTimeLeft}
          modules={[Autoplay, EffectFade]}
          effect="fade"
          fadeEffect={{ crossFade: true }}
        >
          {pathImages.map((item, index) => (
            <SwiperSlide key={index}>
              <AspectRatio ratio={isMobile ? 4 / 3 : 16 / 9}>
                <Box position="relative">
                  <Image
                    src={require(`../../assets/images/${item.path}`)}
                    alt={item.altImage}
                    objectFit="cover"
                    w="100%"
                    loading="lazy"
                    transition="opacity 0.5s ease-in-out"
                  />
                </Box>
              </AspectRatio>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Simple Overlay */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="linear-gradient(to bottom, rgba(10, 10, 10, 0.3) 0%, rgba(10, 10, 10, 0.6) 100%)"
          zIndex={1}
        />
      </Box>

      {/* Main Content Section */}
      <Container maxW="container.xl" px={{ base: "0.3em", md: 8, lg: 12 }}>
        <Container maxW="container.xl" px={{ base: 4, md: 8, lg: 12 }}>
          {/* Prima riga: Titolo e box servizi affiancati su desktop */}
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={{ base: 8, lg: 16 }}
            mb={{ base: 8, lg: 16 }}
          >
            {/* Colonna sinistra: Titolo, testo e bottone */}
            <Box>
              <Heading
                as="h1"
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                mb={6}
                lineHeight="1.2"
                color={textColor}
              >
                Service Luci a Firenze per Eventi Indimenticabili
              </Heading>

              <Text
                fontSize={{ base: "md", md: "lg" }}
                color={secondaryTextColor}
                mb={8}
                lineHeight="1.8"
              >
                Dal 1989, Controluce Illuminazione progetta e realizza impianti
                illuminotecnici di alta qualità per qualsiasi tipo di evento a
                Firenze e in tutta la Toscana.
              </Text>

              <Button
                as={Link}
                to="/contact"
                colorScheme="orange"
                variant="outline"
                size="lg"
                borderWidth="2px"
                borderColor={accentColor}
                color={accentColor}
                _hover={{
                  bg: "rgba(255, 140, 0, 0.1)",
                  transform: "translateX(4px)",
                  boxShadow: `0 0 20px ${accentColor}33`,
                }}
                _active={{
                  transform: "translateX(2px)",
                }}
                transition="all 0.3s"
                mb={{ base: 8, lg: 0 }}
              >
                Richiedi un preventivo
                <Icon ml={2} viewBox="0 0 24 24" boxSize={5}>
                  <path
                    d="M15.7285 3.88396C17.1629 2.44407 19.2609 2.41383 20.4224 3.57981C21.586 4.74798 21.5547 6.85922 20.1194 8.30009L17.6956 10.7333C17.4033 11.0268 17.4042 11.5017 17.6976 11.794C17.9911 12.0863 18.466 12.0854 18.7583 11.7919L21.1821 9.35869C23.0934 7.43998 23.3334 4.37665 21.4851 2.5212C19.6346 0.663551 16.5781 0.905664 14.6658 2.82536L9.81817 7.69182C7.90688 9.61053 7.66692 12.6739 9.51519 14.5293C9.80751 14.8228 10.2824 14.8237 10.5758 14.5314C10.8693 14.2391 10.8702 13.7642 10.5779 13.4707C9.41425 12.3026 9.44559 10.1913 10.8809 8.75042L15.7285 3.88396Z"
                    fill="currentColor"
                  />
                  <path
                    d="M14.4851 9.47074C14.1928 9.17728 13.7179 9.17636 13.4244 9.46868C13.131 9.76101 13.1301 10.2359 13.4224 10.5293C14.586 11.6975 14.5547 13.8087 13.1194 15.2496L8.27178 20.1161C6.83745 21.556 4.73937 21.5863 3.57791 20.4203C2.41424 19.2521 2.44559 17.1408 3.88089 15.6999L6.30473 13.2667C6.59706 12.9732 6.59614 12.4984 6.30268 12.206C6.00922 11.9137 5.53434 11.9146 5.24202 12.2081L2.81818 14.6413C0.906876 16.5601 0.666916 19.6234 2.51519 21.4789C4.36567 23.3365 7.42221 23.0944 9.33449 21.1747L14.1821 16.3082C16.0934 14.3895 16.3334 11.3262 14.4851 9.47074Z"
                    fill="currentColor"
                  />
                </Icon>
              </Button>
            </Box>

            {/* Colonna destra: Box servizi */}
            <Box
              bg={bgCard}
              borderRadius="xl"
              p={6}
              border="1px solid"
              borderLeft="4px solid"
              borderColor="gray.700"
              _hover={{
                borderColor: borderColor,
                transform: "translateY(-4px)",
                boxShadow: "2xl",
              }}
              transition="all 0.3s"
              height="fit-content"
              alignSelf={{ lg: "start" }}
            >
              <Heading size="md" color={textColor} mb={4}>
                Servizi Inclusi nel Preventivo:
              </Heading>
              <List spacing={3} color={secondaryTextColor}>
                <ListItem>
                  <ListIcon as={CheckCircleIcon} color="orange.400" />
                  Progettazione illuminotecnica personalizzata
                </ListItem>
                <ListItem>
                  <ListIcon as={CheckCircleIcon} color="orange.400" />
                  Noleggio attrezzature professionali
                </ListItem>
                <ListItem>
                  <ListIcon as={CheckCircleIcon} color="orange.400" />
                  Trasporto e logistica
                </ListItem>
                <ListItem>
                  <ListIcon as={CheckCircleIcon} color="orange.400" />
                  Montaggio e allestimento
                </ListItem>
                <ListItem>
                  <ListIcon as={CheckCircleIcon} color="orange.400" />
                  Assistenza tecnica durante l'evento
                </ListItem>
                <ListItem>
                  <ListIcon as={CheckCircleIcon} color="orange.400" />
                  Smontaggio e ritiro
                </ListItem>
              </List>
            </Box>
          </SimpleGrid>

          {/* Seconda riga: Le tre card allineate al centro */}
          <Box display="flex" justifyContent="center" mb={16}>
            <VStack
              spacing={6}
              width="full"
              maxW={{ base: "full", lg: "800px" }}
              align="stretch"
            >
              <Card
                bg={bgCard}
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.700"
                _hover={{
                  borderColor: borderColor,
                  transform: "translateY(-4px)",
                  boxShadow: "2xl",
                }}
                transition="all 0.3s"
              >
                <CardBody p={{ base: 6, md: 8 }}>
                  <Stack spacing={4}>
                    <HStack spacing={3}>
                      <StarIcon color="orange.400" boxSize={5} />
                      <Heading size="md" color={textColor}>
                        Esperienza e Qualità
                      </Heading>
                    </HStack>
                    <Text color={secondaryTextColor} lineHeight="1.8">
                      <strong style={{ color: textColor }}>
                        Controluce Illuminazione
                      </strong>{" "}
                      è il tuo{" "}
                      <strong style={{ color: textColor }}>
                        service luci a Firenze
                      </strong>{" "}
                      che dal 1989 progetta e realizza strutture e impianti
                      illuminotecnici di alta qualità per qualsiasi tipo di
                      evento. Il nostro{" "}
                      <strong style={{ color: textColor }}>
                        servizio noleggio luci
                      </strong>{" "}
                      comprende attrezzature professionali e moderne per
                      soddisfare ogni esigenza.
                    </Text>
                  </Stack>
                </CardBody>
              </Card>

              <Card
                bg={bgCard}
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.700"
                boxShadow="lg"
                _hover={{
                  borderColor: borderColor,
                  transform: "translateY(-4px)",
                  boxShadow: "2xl",
                }}
                transition="all 0.3s"
              >
                <CardBody p={{ base: 6, md: 8 }}>
                  <Stack spacing={4}>
                    <HStack spacing={3}>
                      <SettingsIcon color="orange.400" boxSize={5} />
                      <Heading size="md" color={textColor}>
                        Servizio Completo
                      </Heading>
                    </HStack>
                    <Text color={secondaryTextColor} lineHeight="1.8">
                      Il{" "}
                      <strong style={{ color: textColor }}>
                        servizio noleggio luci
                      </strong>{" "}
                      che Controluce offre a{" "}
                      <strong style={{ color: textColor }}>Firenze</strong> è
                      "completo" per ridurre al minimo le preoccupazioni del
                      cliente e comprende: progettazione, noleggio, trasporto
                      del materiale, allestimento, assistenza tecnica,
                      disallestimento, più tutte le certificazioni necessarie.
                    </Text>
                  </Stack>
                </CardBody>
              </Card>

              <Card
                bg={bgCard}
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.700"
                _hover={{
                  borderColor: borderColor,
                  transform: "translateY(-4px)",
                  boxShadow: "2xl",
                }}
                transition="all 0.3s"
              >
                <CardBody p={{ base: 6, md: 8 }}>
                  <Stack spacing={4}>
                    <HStack spacing={3}>
                      <UnlockIcon color="orange.400" boxSize={5} />
                      <Heading size="md" color={textColor}>
                        Professionalità e Normative
                      </Heading>
                    </HStack>
                    <Text color={secondaryTextColor} lineHeight="1.8">
                      Come{" "}
                      <strong style={{ color: textColor }}>
                        service luci specializzato a Firenze
                      </strong>
                      , i nostri lavori sono realizzati da personale
                      specializzato e seguendo le normative vigenti, prestando
                      particolare cura nella scelta dei materiali che sono
                      sempre aggiornati e scelti tra i prodotti più innovativi e
                      tecnicamente avanzati.
                    </Text>
                  </Stack>
                </CardBody>
              </Card>
            </VStack>
          </Box>
        </Container>

        {/* Services Features Section */}
        <Box
          bg={bgCard}
          borderRadius="2xl"
          p={{ base: 8, md: 12, lg: 16 }}
          mx={{ base: "1em", xl: "0" }}
          mb={16}
          border="1px solid"
          borderColor="gray.700"
          boxShadow="xl"
        >
          <VStack spacing={{ base: 8, lg: 12 }} align="center">
            <Heading
              as="h2"
              fontSize={{ base: "2xl", md: "3xl" }}
              color={textColor}
              textAlign="center"
            >
              Perché Scegliere Controluce Illuminazione
            </Heading>

            <SimpleGrid
              columns={{ base: 1, md: 2, lg: 4 }}
              spacing={8}
              w="full"
            >
              <VStack align="center" spacing={4} textAlign="center">
                <Box
                  w="16"
                  h="16"
                  bg="rgba(255, 140, 0, 0.2)"
                  borderRadius="xl"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  border="2px solid"
                  borderColor="orange.400"
                >
                  <TimeIcon color="orange.400" boxSize={8} />
                </Box>
                <Heading as="h3" size="md" color={textColor}>
                  Dal 1989
                </Heading>
                <Text color={secondaryTextColor}>
                  Oltre 30 anni di esperienza nel settore dell'illuminazione
                  professionale
                </Text>
              </VStack>

              <VStack align="center" spacing={4} textAlign="center">
                <Box
                  w="16"
                  h="16"
                  bg="rgba(255, 140, 0, 0.2)"
                  borderRadius="xl"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  border="2px solid"
                  borderColor="orange.400"
                >
                  <TriangleUpIcon color="orange.400" boxSize={8} />
                </Box>
                <Heading as="h3" size="md" color={textColor}>
                  Servizio Completo
                </Heading>
                <Text color={secondaryTextColor}>
                  Dalla progettazione allo smontaggio, tutto incluso nel
                  servizio
                </Text>
              </VStack>

              <VStack align="center" spacing={4} textAlign="center">
                <Box
                  w="16"
                  h="16"
                  bg="rgba(255, 140, 0, 0.2)"
                  borderRadius="xl"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  border="2px solid"
                  borderColor="orange.400"
                >
                  <UnlockIcon color="orange.400" boxSize={8} />
                </Box>
                <Heading as="h3" size="md" color={textColor}>
                  Certificazioni
                </Heading>
                <Text color={secondaryTextColor}>
                  Tutte le certificazioni necessarie per sicurezza e conformità
                </Text>
              </VStack>

              <VStack align="center" spacing={4} textAlign="center">
                <Box
                  w="16"
                  h="16"
                  bg="rgba(255, 140, 0, 0.2)"
                  borderRadius="xl"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  border="2px solid"
                  borderColor="orange.400"
                >
                  <CalendarIcon color="orange.400" boxSize={8} />
                </Box>
                <Heading as="h3" size="md" color={textColor}>
                  Disponibilità 24/7
                </Heading>
                <Text color={secondaryTextColor}>
                  Supporto tecnico disponibile prima, durante e dopo l'evento
                </Text>
              </VStack>
            </SimpleGrid>

            <Button
              as={Link}
              to="/contact"
              colorScheme="orange"
              size="lg"
              mt={4}
              bg="linear-gradient(135deg, #FFB300 0%, #FF8F00 100%)"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "2xl",
                bg: "linear-gradient(135deg, #FF8F00 0%, #FF6F00 100%)",
              }}
              transition="all 0.3s"
              px={8}
            >
              <ChatIcon mr={2} />
              Contattaci
            </Button>
          </VStack>
        </Box>

        {/* Final CTA Section */}
        <Box
          borderRadius="2xl"
          bg="linear-gradient(135deg, rgba(255, 179, 0, 0.4) 0%, rgba(255, 143, 0, 0.3) 100%)"
          border="1px solid"
          borderColor="orange.200"
          p={{ base: 8, md: 12 }}
          mx={{ base: "1em", xl: "0" }}
          mb={16}
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top="-50%"
            right="-50%"
            w="full"
            h="full"
            opacity={0.3}
          />

          <VStack
            spacing={6}
            align="center"
            textAlign="center"
            position="relative"
            zIndex={1}
          >
            <Heading as="h2" size="xl" color={textColor}>
              Pronto a Illuminare il Tuo Evento?
            </Heading>

            <Text fontSize="lg" color={secondaryTextColor} maxW="600px">
              Specializzati nel montaggio di strutture americane Eurotruss, che
              sono spesso sostegno dei nostri impianti illuminotecnici.
              Scegliendo il nostro{" "}
              <strong style={{ color: textColor }}>
                service luci a Firenze
              </strong>
              , avrai la garanzia di un{" "}
              <strong style={{ color: textColor }}>
                servizio noleggio luci
              </strong>{" "}
              professionale e affidabile per il tuo evento.
            </Text>

            <HStack spacing={4} mt={4} flexWrap="wrap" justify="center">
              <Button
                as={Link}
                to="/contact"
                colorScheme="orange"
                size="lg"
                leftIcon={<EmailIcon />}
                bg="linear-gradient(135deg, #FFB300 0%, #FF8F00 100%)"
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "2xl",
                }}
                transition="all 0.3s"
              >
                Invia Email
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Container>
    </animated.div>
  );
};

export default Home;
